import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { errorToast } from "../components/general/ToasterContainer";

const API_BASE_URL = process.env.REACT_APP_AWS_API_GATEWAY_BASE_URL

type ITokens = {
  idToken: string;
  accessToken: string;
  refreshToken: string;
};

class ApiService {
  private static axiosInstance: AxiosInstance | null = null;
  private static navigate: ReturnType<typeof useNavigate> | null = null;
  private static location: ReturnType<typeof useLocation> | null = null;

  public static initialize(
    navigate: ReturnType<typeof useNavigate>,
    location: ReturnType<typeof useLocation>
  ) {
    if (!ApiService.axiosInstance) {
      ApiService.axiosInstance = axios.create({
        baseURL: API_BASE_URL,
        withCredentials: true,
        headers: {
          "Access-Control-Allow-Origin": process.env.REACT_APP_ALLOW_ORIGIN,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      ApiService.axiosInstance.interceptors.request.use(
        ApiService.handleRequestSuccess,
        ApiService.handleRequestError
      );

      ApiService.axiosInstance.interceptors.response.use(
        ApiService.handleResponseSuccess,
        ApiService.handleResponseError
      );

      ApiService.navigate = navigate;
      ApiService.location = location;
    }
  }

  private static pathToRedirect = (): string => {
    if (ApiService.location) {
      return ApiService.location.pathname;
    }

    return "/feedbacks";
  };

  private static handleRequestSuccess(config: InternalAxiosRequestConfig) {
    const tokens = getTokens();

    if (tokens && config.headers) {
      config.headers.Authorization = `Bearer ${tokens.idToken}`;
    }

    return config;
  }

  private static handleRequestError(error: AxiosError) {
    return Promise.reject(error);
  }

  private static handleResponseSuccess(response: AxiosResponse) {
    return response;
  }

  private static handleResponseError = async (error: any) => {
    if (error.response) {
      if (
        error.response.status === 403 &&
        ApiService.navigate &&
        ApiService.location
      ) {
        ApiService.navigate("/sign_out", {
          state: { from: ApiService.pathToRedirect() },
        });

        return error.response;
      } else if (error.response.status === 401 && ApiService.navigate) {
        if (ApiService.location) {
          localStorage.setItem('referer', ApiService.location.pathname);
          ApiService.navigate("/", { 
            state: { from: ApiService.pathToRedirect() }, 
            replace: true 
          })
        } else {
          ApiService.navigate("/sign_out", {
            state: { from: ApiService.pathToRedirect() },
          });
        }
        return error.response;
      } else {
        errorToast({
          message: `${error.code} - ${error.message} - ${error.response.data}`,
        });
        console.info("Error Network", error);
        return error.response;
      }
    } else {
      errorToast({ message: `${error.code} - ${error.name} - ${error.message}` });
      console.info('Error Network', error);
      return { data: {}, status: 599 }
    }
  };

  public static get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return ApiService.axiosInstance!.get<T>(url, config);
  }

  public static post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return ApiService.axiosInstance!.post<T, any>(url, data, config);
  }

  public static put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return ApiService.axiosInstance!.put<T, any>(url, data, config);
  }

  public static delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return ApiService.axiosInstance!.delete<T>(url, config);
  }
}

// const renewToken = async () => {
//   console.info("Renewing token...");

//   const tokens = getTokens();
//   const params = new URLSearchParams();

//   params.append("grant_type", "refresh_token ");
//   params.append("client_id", process.env.REACT_APP_COGNITO_CLIENT_ID || "");
//   params.append("refresh_token", tokens.refreshToken);

//   const response = await axios.post(
//     `${process.env.REACT_APP_COGNITO_DOMAIN}/oauth2/token`,
//     params,
//     {
//       headers: {
//         "Content-Type": "application/x-www-form-urlencoded",
//       },
//     }
//   );

//   return response.data;
// };

const getTokens = (): ITokens => {
  const currentTokens: ITokens = {
    idToken: localStorage.getItem("idToken") || "",
    accessToken: localStorage.getItem("accessToken") || "",
    refreshToken: localStorage.getItem("refreshToken") || "",
  };

  // console.log('Current Tokens:', currentTokens);
  return currentTokens;
};

export default ApiService;

// import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
// import { errorToast } from '../components/general/ToasterContainer';
// import { useNavigate } from 'react-router-dom';

// const API_BASE_URL = process.env.REACT_APP_AWS_API_GATEWAY_BASE_URL

// type ITokens = {
//   idToken: string;
//   accessToken: string;
//   refreshToken: string;
// }

// declare module 'axios' {
//   export interface AxiosInstance {
//     request<T = any>(config: AxiosRequestConfig): Promise<T>;
//     get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
//     delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
//     head<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
//     post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
//     put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
//     patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
//   }
// }

// const ApiService: AxiosInstance = axios.create({
//   baseURL: API_BASE_URL,
//   withCredentials: true,
//   headers: {
//     'Access-Control-Allow-Origin': process.env.REACT_APP_ALLOW_ORIGIN,
//     'Content-Type': 'application/json',
//     'Accept': 'application/json',
//   },
// });

// ApiService.interceptors.request.use(
//   (config: InternalAxiosRequestConfig) => {
//     console.log('Axios interceptors', config);
//     const tokens = getTokens();

//     if (tokens && config.headers) {
//       config.headers.Authorization = `Bearer ${tokens.idToken}`;
//     }

//     return config
//   }, (error) => {
//     return Promise.reject(error);
//   }
// );

// ApiService.interceptors.response.use(
//   async (response) => response,
//   async (error) => {
//     const tokens = getTokens();

//     if (error.response) {
//       const originalRequest = error.config

//       if (error.response.status === 403) {
//         console.log('redirecionar');
//         useNavigate()('/')
//       } else if (error.response.status === 401 && !originalRequest._retry) {
//         let accessToken = tokens.accessToken;
//         let refreshToken = tokens.refreshToken;

//         originalRequest._retry = true;

//         try {
//           const newToken = await renewToken().then((response) => {
//             return response.data;
//           }).catch((error) => {
//             console.log(`Error when renew token: ${JSON.stringify(error.response.data)}`, error);
//             return tokens;
//           })

//           console.log('NEW TOKEN', newToken);

//           accessToken = newToken.access_token;
//           refreshToken = newToken.refresh_token;
//           localStorage.setItem('accessToken', accessToken);
//           localStorage.setItem('refreshToken', refreshToken);
//           originalRequest.headers.Authorization = `Bearer ${newToken.id_token}`;

//           return ApiService(originalRequest);
//         } catch (err) {
//           console.error('Erro ao renovar token:', err);
//           // Redirecionar para a tela de login ou tratar o erro de renovação
//         }
//       }

//       return error.response;
//     } else {
//       errorToast({ message: `${error.code} - ${error.name} - ${error.message}` });
//       console.info('Error Network', error);
//       return { data: {}, status: 599 }
//     }
//   },
// );

// const renewToken = async () => {
//   console.info('Renewing token...')

//   const tokens = getTokens()
//   const params = new URLSearchParams();

//   params.append('grant_type', 'refresh_token ');
//   params.append('client_id', process.env.REACT_APP_COGNITO_CLIENT_ID || "");
//   params.append('refresh_token', tokens.refreshToken);

//   const response = await axios.post(`${process.env.REACT_APP_COGNITO_DOMAIN}/oauth2/token`, params, {
//     headers: {
//       'Content-Type': 'application/x-www-form-urlencoded',
//     },
//   });

//   return response.data;
// }

// const getTokens = (): ITokens => {
//   const currentTokens: ITokens = {
//     idToken: localStorage.getItem('idToken') || '',
//     accessToken: localStorage.getItem('accessToken') || '',
//     refreshToken: localStorage.getItem('refreshToken') || ''
//   }

//   console.log('Current Tokens:', currentTokens);

//   return currentTokens
// }

// export default ApiService;
